import { useNavigate } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";

import {
  Container,
  BackButton,
  Title,
  Section,
  SectionTitle,
  CorePrinciplesGrid,
  PrincipleText,
  StepTitle,
  StepContent,
  ImagePlaceholder,
  Column,
  PrincipleTextLight,
} from "./TrainTutorialPage.styles";

import Train01 from "../../assets/images/train1.png";
import Train02 from "../../assets/images/train2.png";
import Train03 from "../../assets/images/train3.png";
import Train04 from "../../assets/images/train4.png";

const TrainTutorialPage = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);

    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const corePrinciples = [
    {
      number: 1,
      text: "Train the motion, not the ID. This template is for image2video: the person comes from the first frame image; training only teaches how it moves.",
    },
    {
      number: 2,
      text: "The generated video is always 5s, so your action must complete within five seconds.",
    },
    {
      number: 3,
      text: "The model learns from Training Videos, not from the Prompt. Training videos determine the result; the prompt is only a concise action description.",
    },
    {
      number: 4,
      text: "Consistency first. Quality > quantity. Use the same action and rhythm across clips. It's better to have 5 clean clips than 20 noisy, mismatched ones.",
    },
    {
      number: 5,
      text: 'Improve generalization with diverse starting states (e.g., front/back starts, single/dual subjects, "no X → has X" transitions), while keeping the core action identical.',
    },
    {
      number: 6,
      text: "Attributes such as bust shape/size, color tones, hairy vs. non-hairy, facial expression, etc. are determined by the data distribution in your training videos.",
    },
  ];

  return (
    <MainContainer>
      <BackButton onClick={handleBack}>
        <ChevronSvg />
        Back to Train
      </BackButton>
      <Container>
        <Title>Train Tutorial</Title>
        <Section>
          <SectionTitle>Core Principles</SectionTitle>
          <CorePrinciplesGrid>
            {corePrinciples.map((principle) => (
              <PrincipleText>• {principle.text}</PrincipleText>
            ))}
          </CorePrinciplesGrid>
        </Section>

        <Section>
          <StepTitle>1. Train Model</StepTitle>
          <StepContent>
            <ImagePlaceholder src={Train01} alt="Train tutorial image 01" />

            <Column>
              <SectionTitle>What to fill in</SectionTitle>

              <Column $small>
                <PrincipleText>• Task Name</PrincipleText>
                <PrincipleText>Internal identifier.</PrincipleText>
              </Column>

              <Column $small>
                <PrincipleText>• Prompt</PrincipleText>
                <PrincipleText>
                  Write a clear action description that exactly matches the
                  videos, for example:
                </PrincipleText>
                <PrincipleText>
                  The woman turns around and walks away.
                </PrincipleText>
              </Column>

              <Column $small>
                <PrincipleText>• Training Videos</PrincipleText>
                <PrincipleText>
                  Every clip must show the same action (exactly matching the
                  prompt). Recommended constraints: 10~20 clips, 3~5 s each,
                  resolution ≥ 360p, ≤ 20 MB per clip.
                </PrincipleText>
              </Column>

              <Column $small>
                <PrincipleText>• Testing Images (6~12)</PrincipleText>
                <PrincipleText>
                  Pick 6~12 different image from the library to verify that the
                  learned motion transfers to new IDs.
                </PrincipleText>
              </Column>
            </Column>
            <ImagePlaceholder
              style={{ marginTop: 16 }}
              src={Train02}
              alt="Train tutorial image 02"
            />
          </StepContent>
        </Section>

        <Section>
          <StepTitle>2. Make and Use Template</StepTitle>

          <StepContent>
            <ImagePlaceholder src={Train03} alt="Train tutorial image 03" />

            <Column>
              <SectionTitle>What to fill in</SectionTitle>

              <Column $small>
                <PrincipleText>• Template Title</PrincipleText>
                <PrincipleText>Displayed under Your Template.</PrincipleText>
              </Column>

              <Column $small>
                <PrincipleText>• Cover Video</PrincipleText>
                <PrincipleTextLight>
                  Choose one
                  <PrincipleText> test-result video </PrincipleText>that best
                  represents the complete action as the template cover.
                </PrincipleTextLight>
                <PrincipleTextLight $marginTop>
                  You can now generate with your template. You can also publish
                  your template and earn credits.
                </PrincipleTextLight>
              </Column>
            </Column>
          </StepContent>
        </Section>

        <Section>
          <StepTitle>3. Publish Template</StepTitle>

          <StepContent>
            <ImagePlaceholder src={Train04} alt="Train tutorial image 04" />

            <Column>
              <SectionTitle>What to fill in</SectionTitle>

              <Column $small>
                <PrincipleText>• Template Title</PrincipleText>
                <PrincipleText>Displayed under Your Template.</PrincipleText>
              </Column>

              <Column $small>
                <PrincipleText>• Author Name</PrincipleText>
                <PrincipleText>
                  Displayed under Your Template. Required the first time you
                  publish and cannot be changed later.
                </PrincipleText>
              </Column>

              <SectionTitle $marginTop>Review & Publishing Notes</SectionTitle>
              <Column $small>
                <PrincipleText>
                  • Templates must pass our team's review before becoming
                  publicly available
                </PrincipleText>
                <PrincipleText>
                  • You’ll earn 2 credits for each user generation using your
                  template
                </PrincipleText>
                <PrincipleText>
                  • We review for quality standards and potential portrait
                  rights infringement
                </PrincipleText>
                <PrincipleText>
                  • Template approval is not guaranteed and we reserve final
                  discretion to remove templates at any time
                </PrincipleText>
                <PrincipleText>
                  • Once published, your template (including the model and cover
                  image/video) will be permanently public and visible to all
                  users, cannot be manually deleted or removed with account
                  deletion, and this process is irreversible
                </PrincipleText>
              </Column>
            </Column>
          </StepContent>
        </Section>
        <Column $small>
          <SectionTitle>One-line Summary</SectionTitle>
          <PrincipleText>
            • Good training videos are all you need.
          </PrincipleText>
        </Column>
      </Container>
    </MainContainer>
  );
};

export default TrainTutorialPage;
